import React, { FunctionComponent, useState } from 'react';
import { MBSRecipient, MBSGroupMember } from './models';
import { CustomCheckbox } from '../../../common/components/MurphyCheckbox';
import Sms from "@murphy-frontend/web-core/assets/icons/sms.svg";
import Notification from "@murphy-frontend/web-core/assets/icons/noti.svg";
import Email from "@murphy-frontend/web-core/assets/icons/email.svg";
import { RiArrowDropDownLine, RiArrowDropUpLine } from 'react-icons/ri';

interface MBSConfirmationProps {
  messageTitle: string,
  messageBody: string,
  messageQuestion: string,
  questionRequired: boolean,
  recipients: MBSRecipient[],
  translations: Record<string, string>,
  criticalAlert?: boolean,
  mobileChecked?: boolean,
  notificationChecked?: boolean,
  onClickRecipient?: (recipient: MBSRecipient | MBSGroupMember) => void,
  emailChecked?: boolean,
  showValidationErrors?: boolean
}

const styles = {
  subContainer: {
    borderRadius: 10,
    border: '1px solid #EBEBEB',
    width: '70rem',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 10,
    alignItems: 'center'
  },
  buttonContainer: {
    border: '1px solid #EBEBEB',
    width: '15rem',
    marginLeft: 10,
    padding: 10,
    display: 'flex',
    alignItems: 'center'
  },
  recipientContainer: {
    border: '1px solid #EBEBEB',
    justifyContent: 'space-between',
    width: '20rem',
    cursor: 'pointer',
    padding: 10,
    display: 'flex',
    marginBottom: 10,
    marginRight: 10,
    alignItems: 'center'
  },
  container: {
    paddingLeft: 20,
    paddingRight: 20,
    overflow: 'hidden',
  }
};

const MBSConfirmation: FunctionComponent<MBSConfirmationProps> = ({
  messageTitle,
  messageBody,
  messageQuestion,
  questionRequired,
  recipients,
  translations,
  criticalAlert,
  mobileChecked,
  notificationChecked,
  onClickRecipient,
  emailChecked,
  showValidationErrors
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const expandedRecipients: MBSGroupMember[] = [];

  recipients.forEach(rec => {
    if (rec.isGroup && rec.groupMembers?.length > 0) {
      expandedRecipients.push(...rec.groupMembers);
    } else if (!rec.isGroup) {
      expandedRecipients.push({
        id: rec.id.toString(),
        name: rec.name,
        email: rec.email,
        mobile: rec.mobile,
        deviceToken: rec.deviceToken,
        mobileChecked: rec.mobileChecked,
        emailChecked: rec.emailChecked,
        notificationChecked: rec.notificationChecked,
        type: rec.type
      });
    }
  });

  const deduped = Object.values(
    expandedRecipients.reduce((acc, curr) => {
      const key = curr.type === 'user'
        ? curr.customeruserid?.toLowerCase() || curr.id
        : curr.id;
      acc[key] = curr;
      return acc;
    }, {} as Record<string, MBSGroupMember>)
  );

  const channels = [
    {
      key: 'sms',
      label: translations.sms || "SMS",
      icon: <Sms />,
      isSelected: mobileChecked,
    },
    {
      key: 'notification',
      label: translations.notification || "Notification",
      icon: <Notification />,
      isSelected: notificationChecked,
    },
    {
      key: 'email',
      label: translations.email || "Email",
      icon: <Email />,
      isSelected: emailChecked,
    },
  ];

  const noChannelsSelected = !channels.some(c => c.isSelected);
  const line = <div style={{ height: 1, backgroundColor: '#EBEBEB', width: '100%' }} />;

  return (
    <div style={styles.container}>
      <label style={{ fontSize: '18px', marginBottom: 20, color: 'grey', marginTop: 20, display: 'flex' }}>
        {translations.reviewalert || "Review the alert"}
      </label>

      <div style={styles.subContainer}>
        {/* Type and critical alert */}
        <div style={{ display: 'flex', paddingTop: 10, paddingBottom: 10, width: '60rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ color: '#626262' }}>{translations.typ || "Type"}</label>
            <label>{questionRequired ? translations.twoway || "2-way" : translations.oneway || "1-way"}</label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ color: '#626262' }}>{translations.criticalAlert || "Critical Alert"}</label>
            <CustomCheckbox onChange={() => {}} checked={criticalAlert} checkedColor='#00A098' unCheckedColor='#7F7F7F' />
          </div>
        </div>

        {line}

        {/* Channels */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 10,
          paddingBottom: 10,
          width: '60rem',
        }}>
          <label style={{ color: '#626262' }}>{translations.channels || "Channels"}</label>
          <div>
            <div style={{ display: 'flex', width: '45rem' }}>
              {channels.filter(c => c.isSelected).map(channel => (
                <div key={channel.key} style={styles.buttonContainer}>
                  {channel.icon}
                  <label style={{ marginLeft: 10 }}>{channel.label}</label>
                </div>
              ))}
            </div>
            {showValidationErrors && noChannelsSelected && (
              <label style={{ color: 'red', marginTop: 5 }}>
                {translations.mustSelectChannel || "You must select at least one channel."}
              </label>
            )}
          </div>
        </div>

        {line}

        {/* Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 10,
          paddingBottom: 10,
          width: '60rem'
        }}>
          <label style={{ color: '#626262' }}>{translations.title || "Title"}</label>
          <div style={{ width: '44rem' }}>
            <label>{messageTitle}</label>
            {showValidationErrors && !messageTitle?.trim() && (
              <label style={{ color: 'red' }}>
                {translations.mustFillTitle || "You must fill in a title."}
              </label>
            )}
          </div>
        </div>

        {line}

        {/* Message */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 10,
          paddingBottom: 10,
          width: '60rem'
        }}>
          <label style={{ color: '#626262' }}>{translations.message || "Message"}</label>
          <div style={{ width: '44rem' }}>
            <label>{messageBody}</label>
            {showValidationErrors && !messageBody?.trim() && (
              <label style={{ color: 'red' }}>
                {translations.mustFillMessage || "You must fill in a message."}
              </label>
            )}
          </div>
        </div>

        {questionRequired && line}

        {/* Question */}
        {questionRequired && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 10,
            paddingBottom: 10,
            width: '60rem'
          }}>
            <label style={{ color: '#626262' }}>{translations.question || "Question"}</label>
            <label style={{ width: '44rem' }}>{messageQuestion}</label>
          </div>
        )}

        {line}

        {/* Recipients */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 10, width: '60rem' }}>
          <label style={{ color: '#626262' }}>{translations.recipients || "Recipients"}</label>
          <div style={{ width: '44rem', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: 5 }}>{translations.recipientslist || "Custom recipient list"} ({deduped.length})</label>
              {!isExpanded
                ? <RiArrowDropDownLine title="Expand list" onClick={() => setIsExpanded(true)} size={30} />
                : <RiArrowDropUpLine title="Collapse list" onClick={() => setIsExpanded(false)} size={30} />}
            </div>
            {showValidationErrors && deduped.length === 0 && (
              <label style={{ color: 'red', marginTop: 5 }}>
                {translations.mustChooseRecipients || "You must choose at least one recipient."}
              </label>
            )}
            {isExpanded && (
              <div style={{ width: '44rem', display: 'flex', flexWrap: 'wrap', maxHeight: '15rem', overflowY: 'auto' }}>
                {deduped.map((item, idx) => (
                  <div key={`${item.id}-${item.type}-${idx}`} style={styles.recipientContainer} onClick={() => onClickRecipient?.(item)}>
                    <div style={{ display: 'flex' }}>
                      <CustomCheckbox onChange={() => {}} checked={true} checkedColor='#00A098' />
                      <label style={{ marginLeft: 10, width: '12rem', overflow: 'hidden', textOverflow: "ellipsis", whiteSpace: 'nowrap', cursor: 'pointer' }}>{item.name}</label>
                    </div>
                    <div style={{ display: 'flex' }}>
                      {item.mobileChecked && item.mobile && <Sms />}
                      {item.emailChecked && item.email && <Email />}
                      {item.notificationChecked && item.deviceToken && <Notification />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MBSConfirmation;
